import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Trash2, Eye, AlertCircle, Paperclip, Send, Users, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';


function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Por favor, inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function CommunicationsPage() {
  const { isCompany } = useAuth();
  const navigate = useNavigate();
  const [communications, setCommunications] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    files: [],
    sendToAll: true,
    selectedWorkers: []
  });

  useEffect(() => {
    fetchCommunications();
    if (isCompany) {
      fetchWorkers();
    }
  }, [isCompany]);

  const fetchCommunications = async () => {
    try {
      const response = await API.get(`/communications`);
      setCommunications(response.data);
    } catch (err) {
      console.error('Error fetching communications:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await API.get('/workers');
      setWorkers(response.data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  };

  const toggleWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      selectedWorkers: prev.selectedWorkers.includes(workerId)
        ? prev.selectedWorkers.filter(id => id !== workerId)
        : [...prev.selectedWorkers, workerId]
    }));
  };

  const handleCreateCommunication = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      // Create communication with target workers
      const targetWorkers = formData.sendToAll ? null : formData.selectedWorkers;
      const response = await API.post(`/communications`, {
        subject: formData.subject,
        content: formData.content,
        target_workers: targetWorkers
      });

      const commId = response.data.id;

      // Upload attachments if any
      if (formData.files.length > 0) {
        for (const file of formData.files) {
          const data = new FormData();
          data.append('file', file);
          await API.post(`/communications/${commId}/attachment`, data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      setDialogOpen(false);
      setFormData({ subject: '', content: '', files: [], sendToAll: true, selectedWorkers: [] });
      setFormData({ subject: '', content: '', files: [] });
      fetchCommunications();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCommunication = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/communications/${deleteId}`);
      setDeleteId(null);
      fetchCommunications();
    } catch (err) {
      console.error('Error deleting communication:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" >
            Comunicados
          </h1>
          <p className="text-gray-500 mt-1">
            {isCompany ? 'Envía comunicados a tus trabajadores' : 'Comunicados de tu empresa'}
          </p>
        </div>
        {isCompany && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" data-testid="create-communication-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Comunicado
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle >Nuevo Comunicado</DialogTitle>
                <DialogDescription>Redacta el comunicado para tus trabajadores.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCommunication} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Asunto</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Asunto del comunicado"
                    required
                    data-testid="communication-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Mensaje</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Escribe tu mensaje aquí..."
                    rows={5}
                    required
                    data-testid="communication-content-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Adjuntos (opcional)</Label>
                  <Input
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setFormData({ ...formData, files: Array.from(e.target.files) })}
                    className="cursor-pointer"
                    data-testid="communication-files-input"
                  />
                  {formData.files.length > 0 && (
                    <p className="text-sm text-gray-500">{formData.files.length} archivo(s) seleccionado(s)</p>
                  )}
                </div>
                
                {/* Destinatarios */}
                <div className="space-y-3 pt-2 border-t border-orange-100">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Destinatarios</Label>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sendToAll"
                      checked={formData.sendToAll}
                      onCheckedChange={(checked) => setFormData({ ...formData, sendToAll: checked, selectedWorkers: [] })}
                    />
                    <label htmlFor="sendToAll" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Enviar a todos los trabajadores
                    </label>
                  </div>
                  
                  {!formData.sendToAll && (
                    <div className="border border-orange-100 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                      {workers.length === 0 ? (
                        <p className="text-sm text-gray-500">No hay trabajadores</p>
                      ) : (
                        workers.map((worker) => (
                          <div key={worker.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`worker-comm-${worker.id}`}
                              checked={formData.selectedWorkers.includes(worker.id)}
                              onCheckedChange={() => toggleWorker(worker.id)}
                            />
                            <label htmlFor={`worker-comm-${worker.id}`} className="text-sm text-gray-700 flex items-center gap-2">
                              <User className="w-3 h-3 text-gray-400" />
                              {worker.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  
                  {!formData.sendToAll && formData.selectedWorkers.length > 0 && (
                    <p className="text-xs text-gray-500">
                      {formData.selectedWorkers.length} trabajador(es) seleccionado(s)
                    </p>
                  )}
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" 
                    disabled={sending}
                    data-testid="submit-communication-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Enviando...' : 'Enviar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Communications List */}
      <div className="space-y-4">
        {communications.length === 0 ? (
          <div className="bg-white border border-orange-100 rounded-xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-orange-200 mx-auto mb-4" />
            <p className="text-gray-500">No hay comunicados</p>
          </div>
        ) : (
          communications.map((comm) => (
            <div 
              key={comm.id} 
              className="bg-white border border-orange-100 rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate(isCompany ? `/company/communications/${comm.id}` : `/worker/communications/${comm.id}`)}
              data-testid={`communication-card-${comm.id}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900" >
                      {comm.subject}
                    </h3>
                    <p className="text-gray-500 mt-2 line-clamp-2">{comm.content}</p>
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-xs text-gray-400 font-mono">
                        {new Date(comm.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {comm.attachments && comm.attachments.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Paperclip className="w-3 h-3" />
                          {comm.attachments.length} adjunto(s)
                        </span>
                      )}
                      {comm.reply_count > 0 && (
                        <span className="text-xs text-blue-600 font-medium">
                          {comm.reply_count} respuesta(s)
                        </span>
                      )}
                    </div>
                  </div>
                  {isCompany && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(comm.id);
                      }}
                      data-testid={`delete-communication-${comm.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comunicado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el comunicado y todas sus respuestas. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteCommunication}
              data-testid="confirm-delete-communication"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
